import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  getEmployees,
  deleteEmployee,
  clearError,
  clearSuccess,
  updateEmployee,
  getEmployeeDetails,
} from "../../../../redux/Slices/authSlice";
import {
  FaSearch,
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaToggleOn,
  FaToggleOff,
  FaPrint,
  FaFileExcel,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useTitle } from "../../../../shared/hooks/TitleContext";
import {
  chunkArray,
  toPositiveInteger,
} from "../../../../shared/utils.js/utils";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import PdfPreviewModal from "../../Accounting/Reports/components/PdfPreviewModal";
import SortOrderSelect from "../../../../shared/components/SortOrderSelect";
import {
  getGenericResponseCache,
  isOnline,
} from "../../../../shared/utils.js/offlineQueue";
import { BASE_URL } from "../../../../shared/utils.js/utils";

const Employees = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setTitle } = useTitle();
  const { t, i18n } = useTranslation("global");
  const isRTL = i18n.language === "ar";
  const locale = "en-US";
  const dir = isRTL ? "rtl" : "ltr";
  const textAlign = isRTL ? "right" : "left";
  const { employees, employeesPagination, isLoading, error, success } =
    useSelector(state => state.auth);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10); // القيمة المطبقة فعلياً
  const [perPageInput, setPerPageInput] = useState(10); // القيمة الظاهرة في الـ UI
  const [sortOrder, setSortOrder] = useState("asc"); // 'asc' أو 'desc'
  const [sortField, setSortField] = useState("name"); // 'name' | 'employee_code' | 'position' | 'department' | 'is_active'
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  const [employeeForPrint, setEmployeeForPrint] = useState(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfType, setPdfType] = useState(null); // 'single' or 'all'
  const [pdfChunks, setPdfChunks] = useState([]); // For paginated PDF generation
  const PDF_ITEMS_PER_PAGE_PREVIEW = 35; // عدد العناصر في كل صفحة PDF للمعاينة
  const PDF_ITEMS_PER_PAGE_DOWNLOAD = 35; // عدد العناصر في كل صفحة PDF للتنزيل
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [allEmployeesForPreview, setAllEmployeesForPreview] = useState([]); // All employees for preview
  const [singleEmployeeForPreview, setSingleEmployeeForPreview] =
    useState(null); // Single employee for preview
  const [isSinglePreviewOpen, setIsSinglePreviewOpen] = useState(false);
  const lastErrorRef = useRef({ message: null, time: 0 });
  const { employeeDetails } = useSelector(state => state.auth);
  const publicUrl = process.env.PUBLIC_URL || "";
  const headerLogo = `${publicUrl}/ACE.png`;
  const [allCachedEmployees, setAllCachedEmployees] = useState([]); // All employees from cache for offline filtering

  useEffect(() => {
    setTitle("sidenav.hr.employees");
  }, [setTitle]);

  const handleApplyPerPage = () => {
    const safeValue = toPositiveInteger(perPageInput, perPage);
    setPerPageInput(safeValue);
    setPerPage(safeValue);
    setCurrentPage(1);
  };

  // دالة ذكية لتقسيم البيانات بناءً على ارتفاع الصفوف المتوقع لتجنب تجاوز مساحة الصفحة
  const smartChunkArray = items => {
    const AVAILABLE_HEIGHT = 750; // المساحة المتاحة للجدول في الصفحة
    let currentChunk = [];
    let currentHeight = 0;
    const newChunks = [];

    items.forEach(emp => {
      // حساب تقريبي لعدد الأسطر التي سيأخذها كل حقل بناءً على عرضه في الجدول
      const nameL = Math.ceil((emp.name?.length || 0) / 18) || 1;
      const emailL = Math.ceil((emp.email?.length || 0) / 20) || 1;
      const posL = Math.ceil((emp.position?.length || 0) / 15) || 1;
      const depL = Math.ceil((emp.department?.length || 0) / 15) || 1;
      const phoneL = Math.ceil(String(emp.phone || "").length / 12) || 1;
      const idL = Math.ceil(String(emp.national_id || "").length / 14) || 1;

      // وضع حد أقصى للأسطر لتجنب حسابات خاطئة للأسماء أو النصوص الطويلة جداً
      const lines = Math.min(
        Math.max(1, nameL, emailL, posL, depL, phoneL, idL),
        5,
      );

      // حساب الارتفاع بدقة: padding 10px + line-height 12px
      const rowHeight = 10 + lines * 12;

      if (
        currentHeight + rowHeight > AVAILABLE_HEIGHT &&
        currentChunk.length > 0
      ) {
        newChunks.push(currentChunk);
        currentChunk = [];
        currentHeight = 0;
      }
      currentChunk.push(emp);
      currentHeight += rowHeight;
    });
    if (currentChunk.length > 0) newChunks.push(currentChunk);
    return newChunks;
  };

  // Load all employees from cache for filtering (works both online and offline)
  useEffect(() => {
    const loadCachedEmployees = async () => {
      try {
        const url = `${BASE_URL}/employees`;
        // Try different per_page values to find cached data (dynamic total support)
        const perPageOptions = [10000, 5000, 2000, 1000, 500, 200, 100, 50, 10];
        let cachedData = null;
        let foundPerPage = null;

        // Try to find cached data with any per_page value
        for (const perPage of perPageOptions) {
          const params = {
            page: 1,
            per_page: perPage,
            language: i18n.language,
          };

          try {
            const data = await getGenericResponseCache(url, params);
            if (data) {
              // Check if data actually contains employees
              let employeesData = [];
              if (Array.isArray(data.data)) {
                employeesData = data.data;
              } else if (data.data && Array.isArray(data.data.data)) {
                employeesData = data.data.data;
              } else if (
                data.data &&
                data.data.data &&
                Array.isArray(data.data.data)
              ) {
                employeesData = data.data.data;
              } else if (Array.isArray(data)) {
                employeesData = data;
              }

              if (Array.isArray(employeesData) && employeesData.length > 0) {
                cachedData = data;
                foundPerPage = perPage;
                break; // Found valid cache, stop searching
              }
            }
          } catch (e) {
            // Continue to next per_page option
            continue;
          }
        }

        if (cachedData) {
          // Handle different response structures
          let employeesData = [];

          if (Array.isArray(cachedData.data)) {
            employeesData = cachedData.data;
          } else if (cachedData.data && Array.isArray(cachedData.data.data)) {
            employeesData = cachedData.data.data;
          } else if (
            cachedData.data &&
            cachedData.data.data &&
            Array.isArray(cachedData.data.data)
          ) {
            employeesData = cachedData.data.data;
          } else if (Array.isArray(cachedData)) {
            employeesData = cachedData;
          }

          if (Array.isArray(employeesData) && employeesData.length > 0) {
            setAllCachedEmployees(employeesData);
          } else {
            setAllCachedEmployees([]);
          }
        } else {
          // If no cached data, try to use current Redux state as fallback
          setAllCachedEmployees([]);
        }
      } catch (error) {
        console.error("Error loading cached employees:", error);
        setAllCachedEmployees([]);
      }
    };

    // Function to reload employees from API (for when coming back online)
    const reloadEmployeesFromAPI = async () => {
      try {
        // First, get total count dynamically
        const firstResult = await dispatch(
          getEmployees({ page: 1, per_page: 10, language: i18n.language }),
        );
        if (getEmployees.fulfilled.match(firstResult)) {
          const response = firstResult.payload;
          const data = response?.data || response;
          const paginationData = data?.meta || data?.pagination || null;
          const total =
            paginationData?.total ||
            (Array.isArray(data?.data)
              ? data.data.length
              : Array.isArray(data)
                ? data.length
                : 0);

          if (total > 0) {
            // Fetch all employees with per_page = total (dynamically)
            await dispatch(
              getEmployees({
                page: 1,
                per_page: total,
                language: i18n.language,
              }),
            );
            // Wait a bit for cache to update
            await new Promise(resolve => setTimeout(resolve, 500));
            // Reload from cache
            await loadCachedEmployees();
          } else {
            // Fallback: use 1000 if total not found
            await dispatch(
              getEmployees({
                page: 1,
                per_page: 1000,
                language: i18n.language,
              }),
            );
            await new Promise(resolve => setTimeout(resolve, 500));
            await loadCachedEmployees();
          }
        }
      } catch (error) {
        console.error("Error reloading employees from API:", error);
        // Fallback: try to reload from cache anyway
        await loadCachedEmployees();
      }
    };

    loadCachedEmployees();

    // Monitor online/offline status changes
    const handleOnlineStatusChange = async () => {
      if (isOnline()) {
        // When coming back online, reload from API to get latest data
        await reloadEmployeesFromAPI();
      } else {
        // When going offline, just reload from cache
        await loadCachedEmployees();
      }
    };

    // Listen for offline requests retried event (when data is synced)
    const handleOfflineRequestsRetried = async event => {
      const detail = event?.detail || {};
      if (detail.count > 0 && detail.results && detail.results.length > 0) {
        // Check if any of the successful retries were for employees (POST, PUT, DELETE)
        const hasEmployeeRequest = detail.results.some(
          r =>
            r.success === true &&
            (r.request?.url?.includes("/employees") ||
              r.request?.url?.includes("/employee")) &&
            (r.request?.method === "POST" ||
              r.request?.method === "PUT" ||
              r.request?.method === "DELETE"),
        );
        if (hasEmployeeRequest && isOnline()) {
          // Wait a bit for server to process the request
          await new Promise(resolve => setTimeout(resolve, 1000));
          // Reload employees after successful sync
          await reloadEmployeesFromAPI();
        }
      }
    };

    window.addEventListener("online", handleOnlineStatusChange);
    window.addEventListener("offline", handleOnlineStatusChange);
    window.addEventListener(
      "offlineRequestsRetried",
      handleOfflineRequestsRetried,
    );

    return () => {
      window.removeEventListener("online", handleOnlineStatusChange);
      window.removeEventListener("offline", handleOnlineStatusChange);
      window.removeEventListener(
        "offlineRequestsRetried",
        handleOfflineRequestsRetried,
      );
    };
  }, [i18n.language, dispatch]);

  const searchTimeoutRef = useRef(null);

  // Fetch employees on component mount, page/perPage change, language change, or search change
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    const delay = searchTerm ? 400 : 0;
    searchTimeoutRef.current = setTimeout(() => {
      dispatch(
        getEmployees({
          page: currentPage,
          per_page: perPage,
          language: i18n.language,
          ...(searchTerm ? { search: searchTerm } : {}),
        }),
      );
    }, delay);
    return () => clearTimeout(searchTimeoutRef.current);
  }, [dispatch, currentPage, perPage, i18n.language, searchTerm]);

  // Fetch all employees when online and cache is empty (to populate cache)
  useEffect(() => {
    const fetchAllEmployeesIfNeeded = async () => {
      if (isOnline() && allCachedEmployees.length === 0) {
        try {
          // First, get total count dynamically
          const firstResult = await dispatch(
            getEmployees({ page: 1, per_page: 10, language: i18n.language }),
          );
          if (getEmployees.fulfilled.match(firstResult)) {
            const response = firstResult.payload;
            const data = response?.data || response;
            const paginationData = data?.meta || data?.pagination || null;
            const total =
              paginationData?.total ||
              (Array.isArray(data?.data)
                ? data.data.length
                : Array.isArray(data)
                  ? data.length
                  : 0);

            if (total > 0) {
              // Fetch all employees with per_page = total (dynamically)
              await dispatch(
                getEmployees({
                  page: 1,
                  per_page: total,
                  language: i18n.language,
                }),
              );
              // Wait a bit for cache to update
              await new Promise(resolve => setTimeout(resolve, 500));
              // Reload from cache
              const url = `${BASE_URL}/employees`;
              const params = {
                page: 1,
                per_page: total,
                language: i18n.language,
              };
              const cachedData = await getGenericResponseCache(url, params);
              if (cachedData) {
                let employeesData = [];
                if (Array.isArray(cachedData.data)) {
                  employeesData = cachedData.data;
                } else if (
                  cachedData.data &&
                  Array.isArray(cachedData.data.data)
                ) {
                  employeesData = cachedData.data.data;
                } else if (
                  cachedData.data &&
                  cachedData.data.data &&
                  Array.isArray(cachedData.data.data)
                ) {
                  employeesData = cachedData.data.data;
                } else if (Array.isArray(cachedData)) {
                  employeesData = cachedData;
                }
                if (Array.isArray(employeesData) && employeesData.length > 0) {
                  setAllCachedEmployees(employeesData);
                }
              }
            } else {
              // Fallback: use 1000 if total not found
              await dispatch(
                getEmployees({
                  page: 1,
                  per_page: 1000,
                  language: i18n.language,
                }),
              );
              await new Promise(resolve => setTimeout(resolve, 500));
              const url = `${BASE_URL}/employees`;
              const params = {
                page: 1,
                per_page: 1000,
                language: i18n.language,
              };
              const cachedData = await getGenericResponseCache(url, params);
              if (cachedData) {
                let employeesData = [];
                if (Array.isArray(cachedData.data)) {
                  employeesData = cachedData.data;
                } else if (
                  cachedData.data &&
                  Array.isArray(cachedData.data.data)
                ) {
                  employeesData = cachedData.data.data;
                } else if (
                  cachedData.data &&
                  cachedData.data.data &&
                  Array.isArray(cachedData.data.data)
                ) {
                  employeesData = cachedData.data.data;
                } else if (Array.isArray(cachedData)) {
                  employeesData = cachedData;
                }
                if (Array.isArray(employeesData) && employeesData.length > 0) {
                  setAllCachedEmployees(employeesData);
                }
              }
            }
          }
        } catch (error) {
          console.error("Error fetching all employees:", error);
        }
      }
    };
    fetchAllEmployeesIfNeeded();
  }, [allCachedEmployees.length, dispatch, i18n.language]);

  // Handle success and error messages with deduplication
  useEffect(() => {
    if (success) {
      toast.success(success, { rtl: isRTL });
      dispatch(clearSuccess());

      // If success is about adding/updating employee, reload data
      if (
        success.includes("employee") ||
        success.includes("موظف") ||
        success.includes("تم")
      ) {
        // Reload employees from API to get latest data (dynamically based on total)
        const reloadData = async () => {
          try {
            // First, get total count dynamically
            const firstResult = await dispatch(
              getEmployees({ page: 1, per_page: 10, language: i18n.language }),
            );
            if (getEmployees.fulfilled.match(firstResult)) {
              const response = firstResult.payload;
              const data = response?.data || response;
              const paginationData = data?.meta || data?.pagination || null;
              const total =
                paginationData?.total ||
                (Array.isArray(data?.data)
                  ? data.data.length
                  : Array.isArray(data)
                    ? data.length
                    : 0);

              if (total > 0) {
                // Fetch all employees with per_page = total (dynamically)
                await dispatch(
                  getEmployees({
                    page: 1,
                    per_page: total,
                    language: i18n.language,
                  }),
                );
                // Wait a bit for cache to update
                await new Promise(resolve => setTimeout(resolve, 500));
                // Reload from cache
                const url = `${BASE_URL}/employees`;
                const params = {
                  page: 1,
                  per_page: total,
                  language: i18n.language,
                };
                const cachedData = await getGenericResponseCache(url, params);
                if (cachedData) {
                  let employeesData = [];
                  if (Array.isArray(cachedData.data)) {
                    employeesData = cachedData.data;
                  } else if (
                    cachedData.data &&
                    Array.isArray(cachedData.data.data)
                  ) {
                    employeesData = cachedData.data.data;
                  } else if (
                    cachedData.data &&
                    cachedData.data.data &&
                    Array.isArray(cachedData.data.data)
                  ) {
                    employeesData = cachedData.data.data;
                  } else if (Array.isArray(cachedData)) {
                    employeesData = cachedData;
                  }
                  if (
                    Array.isArray(employeesData) &&
                    employeesData.length > 0
                  ) {
                    setAllCachedEmployees(employeesData);
                  }
                }
              } else {
                // Fallback: use 1000 if total not found
                await dispatch(
                  getEmployees({
                    page: 1,
                    per_page: 1000,
                    language: i18n.language,
                  }),
                );
                await new Promise(resolve => setTimeout(resolve, 500));
                const url = `${BASE_URL}/employees`;
                const params = {
                  page: 1,
                  per_page: 1000,
                  language: i18n.language,
                };
                const cachedData = await getGenericResponseCache(url, params);
                if (cachedData) {
                  let employeesData = [];
                  if (Array.isArray(cachedData.data)) {
                    employeesData = cachedData.data;
                  } else if (
                    cachedData.data &&
                    Array.isArray(cachedData.data.data)
                  ) {
                    employeesData = cachedData.data.data;
                  } else if (
                    cachedData.data &&
                    cachedData.data.data &&
                    Array.isArray(cachedData.data.data)
                  ) {
                    employeesData = cachedData.data.data;
                  } else if (Array.isArray(cachedData)) {
                    employeesData = cachedData;
                  }
                  if (
                    Array.isArray(employeesData) &&
                    employeesData.length > 0
                  ) {
                    setAllCachedEmployees(employeesData);
                  }
                }
              }
            }
          } catch (error) {
            console.error("Error reloading employees after success:", error);
          }
        };
        reloadData();
      }
    }
  }, [success, dispatch, isRTL, i18n.language]);

  useEffect(() => {
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      // Only show toast if it's a different error or enough time has passed since last same error (2 seconds)
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, { rtl: isRTL });
        lastErrorRef.current = { message: error, time: now };
      }
      // Clear error after showing
      const timer = setTimeout(() => {
        dispatch(clearError());
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch, isRTL]);

  // Use cached employees if available (for full filtering), otherwise use Redux state
  // This ensures filtering works on all data, not just current page
  const employeesToFilter =
    allCachedEmployees.length > 0 ? allCachedEmployees : employees || [];

  // Apply search filter
  const filteredEmployees = employeesToFilter
    .filter(employee => {
      if (!searchTerm) return true; // Show all if no search term

      const searchLower = searchTerm.toLowerCase();
      return (
        employee.name?.toLowerCase().includes(searchLower) ||
        employee.employee_code?.toString().includes(searchTerm) ||
        employee.position?.toLowerCase().includes(searchLower) ||
        employee.national_id?.toString().includes(searchTerm) ||
        employee.phone?.toString().includes(searchTerm) ||
        employee.email?.toLowerCase().includes(searchLower)
      );
    })
    .sort((a, b) => {
      if (sortField === "is_active") {
        const aVal = a.is_active ? 1 : 0;
        const bVal = b.is_active ? 1 : 0;
        return sortOrder === "asc" ? aVal - bVal : bVal - aVal;
      } else if (sortField === "employee_code") {
        const aVal = (a.employee_code?.toString() || "").toLowerCase();
        const bVal = (b.employee_code?.toString() || "").toLowerCase();
        if (aVal < bVal) return sortOrder === "asc" ? -1 : 1;
        if (aVal > bVal) return sortOrder === "asc" ? 1 : -1;
        return 0;
      } else {
        const field = sortField === "position" ? "position" : sortField === "department" ? "department" : "name";
        const aVal = (a[field]?.toString() || "").toLowerCase();
        const bVal = (b[field]?.toString() || "").toLowerCase();
        if (sortOrder === "asc") return aVal.localeCompare(bVal, isRTL ? "ar" : "en");
        return bVal.localeCompare(aVal, isRTL ? "ar" : "en");
      }
    });

  const handleDelete = employee => {
    setEmployeeToDelete(employee);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (employeeToDelete) {
      await dispatch(deleteEmployee(employeeToDelete.id));
      setShowDeleteModal(false);
      setEmployeeToDelete(null);
    }
  };

  const handleToggleStatus = async employee => {
    try {
      // Store original state for potential rollback
      const originalIsActive = employee.is_active;
      const newIsActive = !employee.is_active;

      // Create FormData similar to EditEmployee
      const formDataToSend = new FormData();
      formDataToSend.append("_method", "PUT");
      formDataToSend.append("is_active", newIsActive ? "1" : "0"); // Toggle the status

      await dispatch(
        updateEmployee({
          id: employee.id,
          employeeData: formDataToSend,
          newIsActive: newIsActive,
          originalIsActive: originalIsActive,
        }),
      ).unwrap();
    } catch (error) {}
  };

  // Handle print single employee
  const handlePrintEmployee = async employee => {
    try {
      if (!employeeDetails || employeeDetails.id !== employee.id) {
        await dispatch(getEmployeeDetails(employee.id)).unwrap();
      }
      setEmployeeForPrint(employee);
      setPdfType("single");
      // Ensure the hidden print template fully renders
      await new Promise(resolve => requestAnimationFrame(resolve));
      await new Promise(resolve => requestAnimationFrame(resolve));

      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      // Preload header/footer/background images to avoid blank captures
      await Promise.all([
        preloadImage(`${publicUrl}/HeaderAce.png`),
        preloadImage(`${publicUrl}/FooterAce.png`),
        preloadImage(headerLogo),
      ]);
      await new Promise(resolve => setTimeout(resolve, 400));

      setIsGeneratingPdf(true);

      const element = document.getElementById(`employee-pdf-${employee.id}`);
      if (!element) {
        toast.error(
          t("employees.print.error", {
            defaultValue: "Error loading print template",
          }),
          { rtl: isRTL },
        );
        return;
      }

      const devicePixelRatio = window.devicePixelRatio || 1;
      const scale = Math.max(3, devicePixelRatio * 2);

      const canvas = await html2canvas(element, {
        scale: scale,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        letterRendering: false,
        allowTaint: false,
        removeContainer: false,
        imageTimeout: 15000,
        onclone: clonedDoc => {
          const clonedElement = clonedDoc.getElementById(
            `employee-pdf-${employee.id}`,
          );
          if (clonedElement) {
            clonedElement.style.fontFamily =
              '"Times New Roman Bold", "Times New Roman", "Times", serif';
            clonedElement.style.fontWeight = "bold";
            clonedElement.style.direction = isRTL ? "rtl" : "ltr";
            clonedElement.style.setProperty(
              "-webkit-font-smoothing",
              "antialiased",
            );
            clonedElement.style.setProperty(
              "-moz-osx-font-smoothing",
              "grayscale",
            );
            clonedElement.style.textRendering = "optimizeLegibility";

            const allElements = clonedElement.querySelectorAll("*");
            allElements.forEach(el => {
              if (el.style) {
                el.style.fontFamily =
                  '"Times New Roman Bold", "Times New Roman", "Times", serif';
                el.style.fontWeight = "bold";
                el.style.setProperty("-webkit-font-smoothing", "antialiased");
                el.style.setProperty("-moz-osx-font-smoothing", "grayscale");
                el.style.textRendering = "optimizeLegibility";
              }
            });
          }
        },
      });

      // Guard against blank canvas
      if (!canvas || canvas.width === 0 || canvas.height === 0) {
        toast.error(
          t("employees.print.error", { defaultValue: "Error creating PDF" }),
          { rtl: isRTL },
        );
        return;
      }

      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgData = canvas.toDataURL("image/png", 1.0);
      let imgWidth = pdfWidth;
      let imgHeight = (canvas.height * pdfWidth) / canvas.width;

      // Professional solution: If content slightly exceeds one page, scale it down to fit
      // This prevents extra blank pages while maintaining quality
      const tolerance = 10; // 10mm tolerance
      if (imgHeight > pdfHeight && imgHeight <= pdfHeight + tolerance) {
        // Scale down to fit exactly on one page
        const scaleFactor = pdfHeight / imgHeight;
        imgHeight = pdfHeight;
        imgWidth = pdfWidth * scaleFactor;

        // Center the scaled image
        const xOffset = (pdfWidth - imgWidth) / 2;
        pdf.addImage(
          imgData,
          "PNG",
          xOffset,
          0,
          imgWidth,
          imgHeight,
          undefined,
          "FAST",
        );
      } else if (imgHeight <= pdfHeight) {
        // Fits perfectly on one page
        pdf.addImage(
          imgData,
          "PNG",
          0,
          0,
          imgWidth,
          imgHeight,
          undefined,
          "FAST",
        );
      } else {
        // Content is too large, split across multiple pages
        let position = 0;
        while (position < imgHeight) {
          if (position > 0) {
            pdf.addPage();
          }
          const remainingHeight = imgHeight - position;
          const pageHeight = Math.min(remainingHeight, pdfHeight);
          pdf.addImage(
            imgData,
            "PNG",
            0,
            -position,
            imgWidth,
            imgHeight,
            undefined,
            "FAST",
          );
          position += pdfHeight;
        }
      }

      const identifier = employee.employee_code || employee.name || employee.id;
      // Force download of the generated PDF (no print dialog)
      const blob = pdf.output("blob");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ACE_Employee_${identifier}_${new Date().toISOString().split("T")[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Employee PDF error:", err);
      toast.error(
        t("employees.print.error", { defaultValue: "Error creating PDF" }),
        { rtl: isRTL },
      );
    } finally {
      setIsGeneratingPdf(false);
      setEmployeeForPrint(null);
      setPdfType(null);
    }
  };

  // Preload image helper
  const preloadImage = src =>
    new Promise(resolve => {
      if (!src) return resolve();
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve();
      img.onerror = () => resolve();
      img.src = src;
    });

  // Handle preview single employee
  const handlePreviewEmployee = async employee => {
    try {
      // Fetch employee details if not already loaded
      if (!employeeDetails || employeeDetails.id !== employee.id) {
        await dispatch(getEmployeeDetails(employee.id)).unwrap();
      }
      setEmployeeForPrint(employee);
      setPdfType("single");
      setSingleEmployeeForPreview(employee);

      // Wait for DOM to update
      await new Promise(resolve => requestAnimationFrame(() => resolve()));
      await new Promise(resolve => setTimeout(resolve, 100));

      setIsSinglePreviewOpen(true);
    } catch (err) {
      console.error("Preview employee error:", err);
      toast.error(
        t("employees.print.error", {
          defaultValue: "Error loading employee data",
        }),
        { rtl: isRTL },
      );
    }
  };

  // Handle preview all employees
  const handlePreviewAll = async () => {
    try {
      // Get total from pagination
      const total =
        employeesPagination?.total || filteredEmployees?.length || 0;

      if (total === 0) {
        toast.error(
          t("employees.print.noData", { defaultValue: "No data to preview" }),
          { rtl: isRTL },
        );
        return;
      }

      // Build params to fetch all data
      const params = {
        page: 1,
        per_page: total,
        language: i18n.language,
      };

      // Fetch all employees
      const result = await dispatch(getEmployees(params));

      // Wait for Redux to update
      await new Promise(resolve => setTimeout(resolve, 100));

      // Try to get data from result payload
      let allEmployees = [];
      if (result.payload?.data?.data) {
        allEmployees = result.payload.data.data;
      } else if (result.payload?.data) {
        allEmployees = Array.isArray(result.payload.data)
          ? result.payload.data
          : [];
      } else if (Array.isArray(result.payload)) {
        allEmployees = result.payload;
      }

      // If still empty, use filteredEmployees from state (should be updated by now)
      if (!Array.isArray(allEmployees) || allEmployees.length === 0) {
        allEmployees = filteredEmployees || [];
      }

      if (!allEmployees || allEmployees.length === 0) {
        toast.error(
          t("employees.print.noData", { defaultValue: "No data to preview" }),
          { rtl: isRTL },
        );
        return;
      }

      const chunks = smartChunkArray(allEmployees);
      setPdfChunks(chunks);
      setAllEmployeesForPreview(allEmployees);
      setPdfType("all");
      setIsPreviewOpen(true);
    } catch (err) {
      console.error("Preview error:", err);
      toast.error(
        t("employees.print.loadError", { defaultValue: "Failed to load data" }),
        { rtl: isRTL },
      );
    }
  };

  // Handle print all employees
  const handlePrintAll = async () => {
    try {
      setIsGeneratingPdf(true);

      // Get total from pagination
      const total =
        employeesPagination?.total || filteredEmployees?.length || 0;

      if (total === 0 || !filteredEmployees || filteredEmployees.length === 0) {
        toast.error(
          t("employees.print.noData", { defaultValue: "No data to print" }),
          { rtl: isRTL },
        );
        setIsGeneratingPdf(false);
        return;
      }

      // Fetch all employees with per_page = total
      const params = {
        page: 1,
        per_page: total,
        language: i18n.language,
      };

      const result = await dispatch(getEmployees(params));

      // Wait for Redux to update
      await new Promise(resolve => setTimeout(resolve, 100));

      // Try to get data from result payload
      let allEmployees = [];
      if (result.payload?.data?.data) {
        allEmployees = result.payload.data.data;
      } else if (result.payload?.data) {
        allEmployees = Array.isArray(result.payload.data)
          ? result.payload.data
          : [];
      } else if (Array.isArray(result.payload)) {
        allEmployees = result.payload;
      }

      // If still empty, use filteredEmployees from state (should be updated by now)
      if (!Array.isArray(allEmployees) || allEmployees.length === 0) {
        allEmployees = filteredEmployees || [];
      }

      if (!allEmployees || allEmployees.length === 0) {
        toast.error(
          t("employees.print.noData", { defaultValue: "No data to print" }),
          { rtl: isRTL },
        );
        setIsGeneratingPdf(false);
        return;
      }

      const chunks = smartChunkArray(allEmployees);
      setPdfChunks(chunks);
      setAllEmployeesForPreview(allEmployees); // Important to pass full list for correct serial numbering across chunks

      setPdfType("all");

      // Wait for React to render the templates
      await new Promise(resolve => requestAnimationFrame(resolve));
      await new Promise(resolve => requestAnimationFrame(resolve));
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      await Promise.all([
        preloadImage(`${publicUrl}/HeaderAce.png`),
        preloadImage(`${publicUrl}/FooterAce.png`),
        preloadImage(headerLogo),
      ]);
      await new Promise(resolve => setTimeout(resolve, 800));

      const pageWidthMm = 210; // A4 width in mm
      let pdf = null;
      const devicePixelRatio = window.devicePixelRatio || 1;
      const scale = Math.min(2, Math.max(1.5, devicePixelRatio));

      // Generate PDF for each chunk - each page sized to its actual content
      for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
        // Wait for DOM update - multiple frames to ensure rendering
        await new Promise(resolve => requestAnimationFrame(resolve));
        await new Promise(resolve => requestAnimationFrame(resolve));
        await new Promise(resolve => setTimeout(resolve, 100));

        const element = document.getElementById(
          `employees-pdf-all-${chunkIndex}`,
        );
        if (!element) {
          if (chunkIndex === 0) {
            setPdfChunks([]);
            toast.error(
              t("employees.print.templateError", {
                defaultValue: "Print template not available",
              }),
              { rtl: isRTL },
            );
            setIsGeneratingPdf(false);
            setPdfType(null);
            return;
          }
          continue;
        }

        const canvas = await html2canvas(element, {
          scale,
          useCORS: true,
          logging: false,
          backgroundColor: "#ffffff",
          letterRendering: false,
          allowTaint: false,
          removeContainer: false,
          imageTimeout: 10000,
          onclone: clonedDoc => {
            const clonedElement = clonedDoc.getElementById(
              `employees-pdf-all-${chunkIndex}`,
            );
            if (clonedElement) {
              clonedElement.style.fontFamily =
                '"Times New Roman Bold", "Times New Roman", "Times", serif';
              clonedElement.style.fontWeight = "bold";
              clonedElement.style.direction = isRTL ? "rtl" : "ltr";
              clonedElement.style.setProperty(
                "-webkit-font-smoothing",
                "antialiased",
              );
              clonedElement.style.setProperty(
                "-moz-osx-font-smoothing",
                "grayscale",
              );
              clonedElement.style.textRendering = "optimizeLegibility";
            }
          },
        });

        const imgData = canvas.toDataURL("image/png", 1.0);
        // Calculate page height based on actual content (no fixed A4 height)
        const pageHeightMm = (canvas.height * pageWidthMm) / canvas.width;

        if (chunkIndex === 0) {
          // Create PDF with first page's actual dimensions
          pdf = new jsPDF("p", "mm", [pageWidthMm, pageHeightMm]);
        } else {
          // Add a new page sized to this chunk's content
          pdf.addPage([pageWidthMm, pageHeightMm]);
        }

        // Add the image to fill the page exactly
        pdf.addImage(
          imgData,
          "PNG",
          0,
          0,
          pageWidthMm,
          pageHeightMm,
          undefined,
          "FAST",
        );
      }

      const fileName = t("employees.export.employeesFileName", {
        defaultValue: isRTL ? "الموظفين" : "Employees",
      });
      // Force download for all employees PDF
      const blob = pdf.output("blob");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ACE_${fileName}_${new Date().toISOString().split("T")[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(
        t("employees.print.success", {
          defaultValue: "Employees printed successfully",
        }),
        { rtl: isRTL },
      );

      // Restore
      setPdfChunks([]);
    } catch (error) {
      console.error("Employees PDF error:", error);
      toast.error(
        t("employees.print.error", {
          defaultValue: "Error creating employee file",
        }),
        { rtl: isRTL },
      );
    } finally {
      setIsGeneratingPdf(false);
      setPdfType(null);
      setPdfChunks([]);
    }
  };

  // Handle export single employee to Excel
  const handleExportEmployeeToExcel = employee => {
    const data = [
      {
        [t("employees.list.table.name", { defaultValue: "Employee Name" })]:
          employee.name || "—",
        [t("employees.list.table.code", { defaultValue: "Employee Code" })]:
          employee.employee_code || "—",
        [t("employees.list.table.username", { defaultValue: "Username" })]:
          employee.username ||
          t("employees.common.notFound", { defaultValue: "Not found" }),
        [t("employees.list.table.email", { defaultValue: "Email" })]:
          employee.email || "—",
        [t("employees.list.table.phone", { defaultValue: "Phone" })]:
          employee.phone || "—",
        [t("employees.list.table.nationalId", { defaultValue: "National ID" })]:
          employee.national_id || "—",
        [t("employees.list.table.birthDate", { defaultValue: "Birth Date" })]:
          employee.birth_date
            ? new Date(employee.birth_date).toLocaleDateString(locale)
            : "—",
        [t("employees.list.table.gender", { defaultValue: "Gender" })]:
          employee.gender === "female"
            ? t("employees.common.gender.female", { defaultValue: "Female" })
            : t("employees.common.gender.male", { defaultValue: "Male" }),
        [t("employees.list.table.department", { defaultValue: "Department" })]:
          employee.department || "—",
        [t("employees.list.table.position", { defaultValue: "Position" })]:
          employee.position || "—",
        [t("employees.list.table.shift", { defaultValue: "Work Shift" })]:
          employee.work_shift ? employee.work_shift.name : "—",
        [t("employees.list.table.status", { defaultValue: "Status" })]:
          employee.is_active
            ? t("employees.common.status.active", { defaultValue: "Active" })
            : t("employees.common.status.inactive", {
                defaultValue: "Inactive",
              }),
      },
    ];

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Employee");
    const identifier = employee.employee_code || employee.name || employee.id;
    const fileName = `Employee_${identifier}_${new Date().toISOString().split("T")[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
    toast.success(
      t("employees.export.success", {
        defaultValue: "Data exported successfully",
      }),
      { rtl: isRTL },
    );
  };

  // Handle export all employees to Excel
  const handleExportAllToExcel = async () => {
    try {
      // Get total from pagination
      const total =
        employeesPagination?.total || filteredEmployees?.length || 0;

      if (total === 0) {
        toast.error(
          t("employees.export.noData", { defaultValue: "No data to export" }),
          { rtl: isRTL },
        );
        return;
      }

      // Fetch all employees
      const params = {
        page: 1,
        per_page: total,
        language: i18n.language,
      };

      const result = await dispatch(getEmployees(params));

      // Wait for Redux to update
      await new Promise(resolve => setTimeout(resolve, 100));

      // Try to get data from result payload
      let allEmployees = [];
      if (result.payload?.data?.data) {
        allEmployees = result.payload.data.data;
      } else if (result.payload?.data) {
        allEmployees = Array.isArray(result.payload.data)
          ? result.payload.data
          : [];
      } else if (Array.isArray(result.payload)) {
        allEmployees = result.payload;
      }

      // If still empty, use filteredEmployees from state (should be updated by now)
      if (!Array.isArray(allEmployees) || allEmployees.length === 0) {
        allEmployees = filteredEmployees || [];
      }

      if (!allEmployees || allEmployees.length === 0) {
        toast.error(
          t("employees.export.noData", { defaultValue: "No data to export" }),
          { rtl: isRTL },
        );
        return;
      }

      const data = allEmployees.map((employee, index) => ({
        [t("employees.serialNumber", { defaultValue: "#" })]: index + 1,
        [t("employees.list.table.name", { defaultValue: "Employee Name" })]:
          employee.name || "—",
        [t("employees.list.table.code", { defaultValue: "Employee Code" })]:
          employee.employee_code || "—",
        [t("employees.list.table.username", { defaultValue: "Username" })]:
          employee.username ||
          t("employees.common.notFound", { defaultValue: "Not found" }),
        [t("employees.list.table.email", { defaultValue: "Email" })]:
          employee.email || "—",
        [t("employees.list.table.phone", { defaultValue: "Phone" })]:
          employee.phone || "—",
        [t("employees.list.table.nationalId", { defaultValue: "National ID" })]:
          employee.national_id || "—",
        [t("employees.list.table.birthDate", { defaultValue: "Birth Date" })]:
          employee.birth_date
            ? new Date(employee.birth_date).toLocaleDateString(locale)
            : "—",
        [t("employees.list.table.gender", { defaultValue: "Gender" })]:
          employee.gender === "female"
            ? t("employees.common.gender.female", { defaultValue: "Female" })
            : t("employees.common.gender.male", { defaultValue: "Male" }),
        [t("employees.list.table.department", { defaultValue: "Department" })]:
          employee.department || "—",
        [t("employees.list.table.position", { defaultValue: "Position" })]:
          employee.position || "—",
        [t("employees.list.table.shift", { defaultValue: "Work Shift" })]:
          employee.work_shift ? employee.work_shift.name : "—",
        [t("employees.list.table.status", { defaultValue: "Status" })]:
          employee.is_active
            ? t("employees.common.status.active", { defaultValue: "Active" })
            : t("employees.common.status.inactive", {
                defaultValue: "Inactive",
              }),
      }));

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      const sheetName = t("employees.export.employeesSheetName", {
        defaultValue: isRTL ? "الموظفين" : "Employees",
      });
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
      const fileNamePrefix = t("employees.export.employeesFileName", {
        defaultValue: isRTL ? "الموظفين" : "Employees",
      });
      const fileName = `${fileNamePrefix}_${new Date().toISOString().split("T")[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      toast.success(
        t("employees.export.success", {
          defaultValue: "Data exported successfully",
        }),
        { rtl: isRTL },
      );
    } catch (error) {
      console.error("Export error:", error);
      toast.error(
        t("employees.export.error", {
          defaultValue: "Error occurred during export",
        }),
        { rtl: isRTL },
      );
    }
  };

  // ...removed loading spinner. Always render the table below, even if isLoading is true.

  return (
    <div
      style={{
        padding: "24px",
        backgroundColor: "#14263b",
        minHeight: "calc(100vh - 80px)",
        color: "white",
        direction: dir,
        textAlign,
        overflowX: "hidden",
        boxSizing: "border-box",
      }}
    >
      {/* ── Row 1: Title + Add ── */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
          gap: "12px",
          flexWrap: "wrap",
        }}
      >
        <h1
          style={{
            fontSize: "22px",
            fontWeight: "bold",
            margin: 0,
            color: "white",
          }}
        >
          {t("employees.list.title")}
        </h1>
        <button
          onClick={() => navigate("/add-employee")}
          style={{
            backgroundColor: "#AC2000",
            color: "white",
            border: "none",
            padding: "10px 18px",
            borderRadius: "8px",
            fontSize: "14px",
            fontWeight: "bold",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            whiteSpace: "nowrap",
          }}
        >
          <FaPlus />
          {t("employees.list.addButton")}
        </button>
      </div>

      {/* ── Row 2: Search + Per-page ── */}
      <div
        style={{
          display: "flex",
          gap: "12px",
          alignItems: "center",
          marginBottom: "12px",
          flexWrap: "wrap",
        }}
      >
        <div style={{ position: "relative", flex: "1", minWidth: "200px" }}>
          <FaSearch
            style={{
              position: "absolute",
              [isRTL ? "right" : "left"]: "14px",
              top: "50%",
              transform: "translateY(-50%)",
              color: "#666",
              fontSize: "14px",
            }}
          />
          <input
            type="text"
            placeholder={t("employees.list.searchPlaceholder")}
            value={searchTerm}
            onChange={e => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            style={{
              width: "100%",
              padding: isRTL ? "10px 42px 10px 14px" : "10px 14px 10px 42px",
              backgroundColor: "#1b3350",
              border: "1px solid #333",
              borderRadius: "8px",
              color: "white",
              fontSize: "14px",
              textAlign: isRTL ? "right" : "left",
              boxSizing: "border-box",
            }}
          />
        </div>
        <label style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 160, color: "white", fontSize: 13, direction: isRTL ? "rtl" : "ltr" }}>
          <span>{t("common.sortBy", { defaultValue: "ترتيب حسب" })}</span>
          <select
            value={sortField}
            onChange={e => setSortField(e.target.value)}
            style={{ padding: "10px 12px", backgroundColor: "#1a1f2e", border: "1px solid #333", borderRadius: "8px", color: "white", fontSize: "14px", direction: isRTL ? "rtl" : "ltr" }}
          >
            <option value="name">{t("employees.list.table.name", { defaultValue: "الاسم" })}</option>
            <option value="employee_code">{t("employees.list.table.code", { defaultValue: "الكود" })}</option>
            <option value="position">{t("employees.list.table.position", { defaultValue: "المسمى الوظيفي" })}</option>
            <option value="department">{t("employees.list.table.department", { defaultValue: "الإدارة" })}</option>
            <option value="is_active">{t("employees.list.table.status", { defaultValue: "الحالة" })}</option>
          </select>
        </label>
        <SortOrderSelect
          value={sortOrder}
          onChange={val => setSortOrder(val)}
        />
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            flexShrink: 0,
          }}
        >
          <label
            style={{ color: "#aaa", fontSize: "13px", whiteSpace: "nowrap" }}
          >
            {t("common.perPage", { defaultValue: "عدد السجلات" })}:
          </label>
          <div style={{ display: "flex", alignItems: "center" }}>
            <button
              onClick={() =>
                setPerPageInput(v =>
                  Math.max(1, toPositiveInteger(v, perPage) - 1),
                )
              }
              style={{
                backgroundColor: "#1b3350",
                color: "white",
                border: "1px solid #333",
                padding: "6px 10px",
                borderRadius: "6px 0 0 6px",
                cursor: "pointer",
                fontSize: "14px",
                minWidth: "30px",
              }}
            >
              −
            </button>
            <input
              type="number"
              min="1"
              step="1"
              value={perPageInput}
              onChange={e =>
                setPerPageInput(toPositiveInteger(e.target.value, perPage))
              }
              style={{
                width: "60px",
                padding: "6px 8px",
                backgroundColor: "#1a1f2e",
                border: "1px solid #333",
                borderLeft: "none",
                borderRight: "none",
                borderRadius: 0,
                color: "white",
                fontSize: "14px",
                textAlign: "center",
              }}
            />
            <button
              onClick={() =>
                setPerPageInput(v => toPositiveInteger(v, perPage) + 1)
              }
              style={{
                backgroundColor: "#1b3350",
                color: "white",
                border: "1px solid #333",
                padding: "6px 10px",
                borderRadius: "0 6px 6px 0",
                cursor: "pointer",
                fontSize: "14px",
                minWidth: "30px",
              }}
            >
              +
            </button>
          </div>
          <button
            onClick={handleApplyPerPage}
            style={{
              backgroundColor: "#AC2000",
              color: "white",
              border: "none",
              padding: "6px 12px",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "13px",
              fontWeight: "bold",
              whiteSpace: "nowrap",
            }}
          >
            {t("common.apply", { defaultValue: "تطبيق" })}
          </button>
        </div>
      </div>

      {/* ── Row 3: Export / Print ── */}
      <div
        style={{
          display: "flex",
          gap: "8px",
          marginBottom: "20px",
          flexWrap: "wrap",
        }}
      >
        <button
          onClick={handlePreviewAll}
          style={{
            backgroundColor: "#444",
            color: "white",
            border: "none",
            padding: "9px 14px",
            borderRadius: "8px",
            fontSize: "13px",
            fontWeight: "bold",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "6px",
          }}
        >
          <FaEye /> {t("employees.preview.all", { defaultValue: "معاينة PDF" })}
        </button>
        <button
          onClick={handlePrintAll}
          disabled={isGeneratingPdf}
          style={{
            backgroundColor: isGeneratingPdf ? "#666" : "#17a2b8",
            color: "white",
            border: "none",
            padding: "9px 14px",
            borderRadius: "8px",
            fontSize: "13px",
            fontWeight: "bold",
            cursor: isGeneratingPdf ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            gap: "6px",
            opacity: isGeneratingPdf ? 0.6 : 1,
          }}
        >
          <FaPrint />{" "}
          {isGeneratingPdf
            ? t("employees.print.generating", { defaultValue: "جاري..." })
            : t("employees.print.all", { defaultValue: "طباعة PDF" })}
        </button>
        <button
          onClick={handleExportAllToExcel}
          style={{
            backgroundColor: "#28a745",
            color: "white",
            border: "none",
            padding: "9px 14px",
            borderRadius: "8px",
            fontSize: "13px",
            fontWeight: "bold",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "6px",
          }}
        >
          <FaFileExcel />{" "}
          {t("employees.export.all", { defaultValue: "تصدير Excel" })}
        </button>
      </div>

      {/* ── Table ── */}
      <div
        style={{
          backgroundColor: "#1b3350",
          borderRadius: "12px",
          border: "1px solid #333",
          overflow: "hidden",
        }}
      >
        <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "separate",
              borderSpacing: 0,
              minWidth: "1840px",
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "#AC2000" }}>
                <th
                  style={{
                    padding: "18px 16px",
                    textAlign: "center",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "14px",
                    borderBottom: "2px solid #333",
                    width: "60px",
                    minWidth: "60px",
                    backgroundColor: "#AC2000",
                  }}
                >
                  #
                </th>
                <th
                  style={{
                    padding: "18px 16px",
                    textAlign: "center",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "14px",
                    borderBottom: "2px solid #333",
                    width: "100px",
                    minWidth: "100px",
                    backgroundColor: "#AC2000",
                  }}
                >
                  {t("employees.list.table.image")}
                </th>
                <th
                  style={{
                    padding: "18px 16px",
                    textAlign: "center",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "14px",
                    borderBottom: "2px solid #333",
                    width: "220px",
                    minWidth: "220px",
                    backgroundColor: "#AC2000",
                  }}
                >
                  {t("employees.list.table.name")}
                </th>
                <th
                  style={{
                    padding: "18px 16px",
                    textAlign: "center",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "14px",
                    borderBottom: "2px solid #333",
                    width: "140px",
                    minWidth: "140px",
                    backgroundColor: "#AC2000",
                  }}
                >
                  {t("employees.list.table.code")}
                </th>
                <th
                  style={{
                    padding: "18px 16px",
                    textAlign: "center",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "14px",
                    borderBottom: "2px solid #333",
                    width: "180px",
                    minWidth: "180px",
                    backgroundColor: "#AC2000",
                  }}
                >
                  {t("employees.list.table.username")}
                </th>
                <th
                  style={{
                    padding: "18px 16px",
                    textAlign: "center",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "14px",
                    borderBottom: "2px solid #333",
                    width: "220px",
                    minWidth: "220px",
                    backgroundColor: "#AC2000",
                  }}
                >
                  {t("employees.list.table.email")}
                </th>
                <th
                  style={{
                    padding: "18px 16px",
                    textAlign: "center",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "14px",
                    borderBottom: "2px solid #333",
                    width: "140px",
                    minWidth: "140px",
                    backgroundColor: "#AC2000",
                  }}
                >
                  {t("employees.list.table.phone")}
                </th>
                <th
                  style={{
                    padding: "18px 16px",
                    textAlign: "center",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "14px",
                    borderBottom: "2px solid #333",
                    width: "160px",
                    minWidth: "160px",
                    backgroundColor: "#AC2000",
                  }}
                >
                  {t("employees.list.table.nationalId", {
                    defaultValue: "National ID",
                  })}
                </th>
                <th
                  style={{
                    padding: "18px 16px",
                    textAlign: "center",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "14px",
                    borderBottom: "2px solid #333",
                    width: "140px",
                    minWidth: "140px",
                    backgroundColor: "#AC2000",
                  }}
                >
                  {t("employees.list.table.birthDate")}
                </th>
                <th
                  style={{
                    padding: "18px 16px",
                    textAlign: "center",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "14px",
                    borderBottom: "2px solid #333",
                    width: "120px",
                    minWidth: "120px",
                    backgroundColor: "#AC2000",
                  }}
                >
                  {t("employees.list.table.gender")}
                </th>
                <th
                  style={{
                    padding: "18px 16px",
                    textAlign: "center",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "14px",
                    borderBottom: "2px solid #333",
                    width: "160px",
                    minWidth: "160px",
                    backgroundColor: "#AC2000",
                  }}
                >
                  {t("employees.list.table.department")}
                </th>
                <th
                  style={{
                    padding: "18px 16px",
                    textAlign: "center",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "14px",
                    borderBottom: "2px solid #333",
                    width: "180px",
                    minWidth: "180px",
                    backgroundColor: "#AC2000",
                  }}
                >
                  {t("employees.list.table.position")}
                </th>
                <th
                  style={{
                    padding: "18px 16px",
                    textAlign: "center",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "14px",
                    borderBottom: "2px solid #333",
                    width: "160px",
                    minWidth: "160px",
                    backgroundColor: "#AC2000",
                  }}
                >
                  {t("employees.list.table.shift")}
                </th>
                <th
                  style={{
                    padding: "18px 16px",
                    textAlign: "center",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "14px",
                    borderBottom: "2px solid #333",
                    width: "120px",
                    minWidth: "120px",
                    backgroundColor: "#AC2000",
                  }}
                >
                  {t("employees.list.table.status")}
                </th>
                <th
                  style={{
                    padding: "18px 16px",
                    textAlign: "center",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "14px",
                    borderBottom: "2px solid #333",
                    width: "220px",
                    minWidth: "220px",
                    backgroundColor: "#AC2000",
                  }}
                >
                  {t("employees.list.table.actions")}
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredEmployees.map((employee, index) => (
                <tr
                  key={employee.id}
                  style={{ borderBottom: "1px solid #333" }}
                >
                  <td
                    style={{
                      padding: "18px 16px",
                      textAlign: "center",
                      color: "white",
                      fontWeight: "bold",
                    }}
                  >
                    {sortOrder === "asc"
                      ? ((employeesPagination?.current_page || 1) - 1) *
                          perPage +
                        index +
                        1
                      : (employeesPagination?.total ||
                          filteredEmployees.length) -
                        (((employeesPagination?.current_page || 1) - 1) *
                          perPage +
                          index)}
                  </td>
                  <td style={{ padding: "18px 16px", textAlign: "center" }}>
                    <div
                      style={{
                        width: "40px",
                        height: "40px",
                        borderRadius: "50%",
                        backgroundColor: "#333",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "0 auto",
                        overflow: "hidden",
                      }}
                    >
                      {employee.images && employee.images.length > 0 ? (
                        <img
                          src={employee.images[0].url}
                          alt=""
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                          onError={e => {
                            e.currentTarget.style.display = "none";
                            const placeholder = document.createElement("span");
                            placeholder.style.color = "#666";
                            placeholder.style.fontSize = "12px";
                            placeholder.textContent = "IMG";
                            e.currentTarget.parentNode?.appendChild(
                              placeholder,
                            );
                          }}
                        />
                      ) : (
                        <span style={{ color: "#666", fontSize: "12px" }}>
                          IMG
                        </span>
                      )}
                    </div>
                  </td>
                  <td
                    style={{
                      padding: "18px 16px",
                      color: "white",
                      textAlign: "center",
                      fontWeight: "500",
                    }}
                  >
                    {employee.name}
                  </td>
                  <td
                    style={{
                      padding: "18px 16px",
                      color: "white",
                      textAlign: "center",
                    }}
                  >
                    {employee.employee_code}
                  </td>
                  <td
                    style={{
                      padding: "18px 16px",
                      color: "white",
                      textAlign: "center",
                    }}
                  >
                    {employee.username ||
                      t("employees.common.notFound", {
                        defaultValue: "Not found",
                      })}
                  </td>
                  <td
                    style={{
                      padding: "18px 16px",
                      color: "white",
                      textAlign: "center",
                    }}
                  >
                    {employee.email}
                  </td>
                  <td
                    style={{
                      padding: "18px 16px",
                      color: "white",
                      textAlign: "center",
                    }}
                  >
                    {employee.phone || "—"}
                  </td>
                  <td
                    style={{
                      padding: "18px 16px",
                      color: "white",
                      textAlign: "center",
                    }}
                  >
                    {employee.national_id || "—"}
                  </td>
                  <td
                    style={{
                      padding: "18px 16px",
                      color: "white",
                      textAlign: "center",
                    }}
                  >
                    {employee.birth_date
                      ? new Date(employee.birth_date).toLocaleDateString(locale)
                      : "—"}
                  </td>
                  <td
                    style={{
                      padding: "18px 16px",
                      color: "white",
                      textAlign: "center",
                    }}
                  >
                    {employee.gender === "female"
                      ? t("employees.common.gender.female")
                      : t("employees.common.gender.male")}
                  </td>
                  <td
                    style={{
                      padding: "18px 16px",
                      color: "white",
                      textAlign: "center",
                    }}
                  >
                    {employee.department}
                  </td>
                  <td
                    style={{
                      padding: "18px 16px",
                      color: "white",
                      textAlign: "center",
                    }}
                  >
                    {employee.position}
                  </td>
                  <td
                    style={{
                      padding: "18px 16px",
                      color: "white",
                      textAlign: "center",
                    }}
                  >
                    {employee.work_shift ? employee.work_shift.name : "-"}
                  </td>
                  <td style={{ padding: "18px 16px", textAlign: "center" }}>
                    <span
                      style={{
                        backgroundColor: employee.is_active
                          ? "#AC2000"
                          : "#dc3545",
                        color: "white",
                        padding: "4px 8px",
                        borderRadius: "20px",
                        fontSize: "11px",
                        fontWeight: "bold",
                        display: "inline-block",
                        minWidth: "60px",
                      }}
                    >
                      {employee.is_active
                        ? t("employees.common.status.active")
                        : t("employees.common.status.inactive")}
                    </span>
                  </td>
                  <td style={{ padding: "18px 16px" }}>
                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        justifyContent: "center",
                        flexWrap: "nowrap",
                      }}
                    >
                      <button
                        onClick={() =>
                          navigate(`/edit-employee/${employee.id}`)
                        }
                        style={{
                          backgroundColor: "#AC2000",
                          color: "white",
                          border: "none",
                          padding: "8px 10px",
                          borderRadius: "6px",
                          cursor: "pointer",
                          fontSize: "13px",
                          flexShrink: 0,
                        }}
                        title={t("employees.list.actions.edit")}
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => handlePreviewEmployee(employee)}
                        style={{
                          backgroundColor: "#666",
                          color: "white",
                          border: "none",
                          padding: "8px 10px",
                          borderRadius: "6px",
                          cursor: "pointer",
                          fontSize: "13px",
                          flexShrink: 0,
                        }}
                        title={t("employees.list.actions.view")}
                      >
                        <FaEye />
                      </button>
                      <button
                        onClick={() => handlePrintEmployee(employee)}
                        disabled={isGeneratingPdf}
                        style={{
                          backgroundColor: isGeneratingPdf ? "#666" : "#17a2b8",
                          color: "white",
                          border: "none",
                          padding: "8px 10px",
                          borderRadius: "6px",
                          cursor: isGeneratingPdf ? "not-allowed" : "pointer",
                          fontSize: "13px",
                          opacity: isGeneratingPdf ? 0.6 : 1,
                          flexShrink: 0,
                        }}
                        title={t("employees.list.actions.print")}
                      >
                        <FaPrint />
                      </button>
                      <button
                        onClick={() => handleExportEmployeeToExcel(employee)}
                        style={{
                          backgroundColor: "#28a745",
                          color: "white",
                          border: "none",
                          padding: "8px 10px",
                          borderRadius: "6px",
                          cursor: "pointer",
                          fontSize: "13px",
                          flexShrink: 0,
                        }}
                        title={t("employees.list.actions.export")}
                      >
                        <FaFileExcel />
                      </button>
                      <button
                        onClick={() => handleDelete(employee)}
                        style={{
                          backgroundColor: "#dc3545",
                          color: "white",
                          border: "none",
                          padding: "8px 10px",
                          borderRadius: "6px",
                          cursor: "pointer",
                          fontSize: "13px",
                          flexShrink: 0,
                        }}
                        title={t("employees.list.actions.delete")}
                      >
                        <FaTrash />
                      </button>
                      <button
                        onClick={() => handleToggleStatus(employee)}
                        style={{
                          backgroundColor: "#34495e",
                          color: "white",
                          border: "none",
                          padding: "8px 10px",
                          borderRadius: "6px",
                          cursor: "pointer",
                          fontSize: "13px",
                          flexShrink: 0,
                        }}
                        title={t("employees.list.actions.toggleStatus")}
                      >
                        {employee.is_active ? <FaToggleOn /> : <FaToggleOff />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* الترقيم */}
        {employeesPagination && (
          <div
            style={{
              padding: "20px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "10px",
              borderTop: "1px solid #333",
            }}
          >
            {/* Previous Button */}
            {employeesPagination.current_page > 1 && (
              <button
                onClick={() =>
                  setCurrentPage(employeesPagination.current_page - 1)
                }
                style={{
                  backgroundColor: "#1b3350",
                  color: "white",
                  border: "1px solid #333",
                  padding: "8px 12px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                &lt;&lt;
              </button>
            )}

            {/* Page Numbers */}
            {Array.from(
              { length: employeesPagination.last_page },
              (_, i) => i + 1,
            ).map(page => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                style={{
                  backgroundColor:
                    page === employeesPagination.current_page
                      ? "#AC2000"
                      : "#1b3350",
                  color: "white",
                  border:
                    page === employeesPagination.current_page
                      ? "none"
                      : "1px solid #333",
                  padding: "8px 12px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight:
                    page === employeesPagination.current_page
                      ? "bold"
                      : "normal",
                }}
              >
                {page}
              </button>
            ))}

            {employeesPagination.current_page <
              employeesPagination.last_page && (
              <button
                onClick={() =>
                  setCurrentPage(employeesPagination.current_page + 1)
                }
                style={{
                  backgroundColor: "#1b3350",
                  color: "white",
                  border: "1px solid #333",
                  padding: "8px 12px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                &gt;&gt;
              </button>
            )}
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div
            className="modal show d-block"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div
                className="modal-content"
                style={{
                  backgroundColor: "#1b3350",
                  border: "1px solid #333",
                  borderRadius: "12px",
                  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
                }}
              >
                <div
                  className="modal-header"
                  style={{ border: "none", borderBottom: "1px solid #333" }}
                >
                  <h5 className="modal-title text-white">
                    {t("employees.list.modal.title")}
                  </h5>
                </div>
                <div className="modal-body text-white">
                  <p>
                    {t("employees.list.modal.description", {
                      name: employeeToDelete?.name || "",
                    })}
                  </p>
                  <p style={{ color: "white" }}>
                    {t("employees.list.modal.warning")}
                  </p>
                </div>
                <div className="modal-footer" style={{ border: "none" }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowDeleteModal(false)}
                    style={{
                      backgroundColor: "#6c757d",
                      border: "none",
                      borderRadius: "6px",
                      padding: "8px 20px",
                    }}
                  >
                    {t("employees.list.modal.cancel")}
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={confirmDelete}
                    style={{
                      backgroundColor: "#dc3545",
                      border: "none",
                      borderRadius: "6px",
                      padding: "8px 20px",
                    }}
                  >
                    {t("employees.list.modal.confirm")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Print Templates */}
      {/* Single Employee Print Template */}
      {employeeForPrint && pdfType === "single" && (
        <div
          id={`employee-pdf-${employeeForPrint.id}`}
          aria-hidden="true"
          style={{
            position: "absolute",
            left: "-9999px",
            top: 0,
            width: "794px",
            height: "1123px",
            backgroundColor: "#ffffff",
            color: "#000000",
            fontFamily:
              '"Times New Roman Bold", "Times New Roman", "Times", serif',
            fontWeight: "bold",
            padding: "40px",
            boxSizing: "border-box",
            overflow: "visible",
          }}
        >
          <div
            style={{
              border: "5px solid #000",
              padding: "3px",
              backgroundColor: "#ffffff",
              boxSizing: "border-box",
              width: "100%",
              minHeight: "1043px",
              height: "auto",
              overflow: "visible",
            }}
          >
            <div
              style={{
                border: "2px solid #000",
                minHeight: "1033px",
                height: "1033px",
                padding: "18px 18px 10px",
                position: "relative",
                display: "flex",
                flexDirection: "column",
                fontFamily:
                  '"Times New Roman Bold", "Times New Roman", "Times", serif',
                fontWeight: "bold",
                WebkitFontSmoothing: "antialiased",
                MozOsxFontSmoothing: "grayscale",
                textRendering: "optimizeLegibility",
                direction: isRTL ? "rtl" : "ltr",
                backgroundColor: "#ffffff",
                boxSizing: "border-box",
                width: "100%",
                overflow: "visible",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  backgroundImage: `url(${headerLogo})`,
                  backgroundSize: "60%",
                  backgroundPosition: "center top",
                  backgroundRepeat: "no-repeat",
                  opacity: 0.05,
                  zIndex: 0,
                  pointerEvents: "none",
                }}
              />
              <div
                style={{
                  position: "relative",
                  zIndex: 1,
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  minHeight: "100%",
                }}
              >
                <img
                  src={`${publicUrl}/HeaderAce.png`}
                  alt="Header"
                  style={{
                    width: "100%",
                    height: "auto",
                    objectFit: "contain",
                    marginBottom: "8px",
                    borderBottom: "4px double #000",
                    paddingBottom: "8px",
                    flexShrink: 0,
                  }}
                  crossOrigin="anonymous"
                />

                <div style={{ textAlign: "center", padding: "6px 0 8px", borderBottom: "1px solid #ddd", marginBottom: "4px", flexShrink: 0, direction: isRTL ? "rtl" : "ltr" }}>
                  <div style={{ fontSize: "16px", fontWeight: "900", color: "#000080" }}>{isRTL ? "تقرير الموظفين" : "Employees Report"}</div>
                  <div style={{ fontSize: "11px", color: "#666", marginTop: "3px" }}>ID: #{employeeForPrint.id}</div>
                </div>

                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "visible",
                    minHeight: 0,
                    paddingBottom: "120px",
                  }}
                >
                  <div
                    style={{
                      marginTop: "12px",
                      border: "2px solid #000",
                      borderRadius: "4px",
                      overflow: "hidden",
                      fontFamily:
                        '"Times New Roman Bold", "Times New Roman", "Times", serif',
                      fontWeight: "bold",
                      backgroundColor: "rgba(255, 255, 255, 0.3)",
                    }}
                  >
                    {(() => {
                      const printEmployee =
                        employeeDetails &&
                        employeeDetails.id === employeeForPrint.id
                          ? employeeDetails
                          : employeeForPrint;
                      const employeeDate = printEmployee.created_at
                        ? new Date(printEmployee.created_at).toLocaleDateString(
                            "en-US",
                            {
                              day: "2-digit",
                              month: "long",
                              year: "numeric",
                            },
                          )
                        : new Date().toLocaleDateString("en-US", {
                            day: "2-digit",
                            month: "long",
                            year: "numeric",
                          });
                      return [
                        {
                          label: t("employees.print.labels.date", {
                            defaultValue: "Date",
                          }),
                          value: employeeDate,
                          accent: true,
                        },
                        {
                          label: t("employees.print.labels.name", {
                            defaultValue: "Employee Name",
                          }),
                          value: printEmployee.name || "—",
                        },
                        {
                          label: t("employees.print.labels.code", {
                            defaultValue: "Employee Code",
                          }),
                          value: printEmployee.employee_code || "—",
                        },
                        {
                          label: t("employees.print.labels.username", {
                            defaultValue: "Username",
                          }),
                          value: printEmployee.username || "—",
                        },
                        {
                          label: t("employees.print.labels.email", {
                            defaultValue: "Email",
                          }),
                          value: printEmployee.email || "—",
                          wrap: true,
                        },
                        {
                          label: t("employees.print.labels.phone", {
                            defaultValue: "Phone",
                          }),
                          value: printEmployee.phone || "—",
                          wrap: true,
                        },
                        {
                          label: t("employees.print.labels.nationalId", {
                            defaultValue: "National ID",
                          }),
                          value: printEmployee.national_id || "—",
                        },
                        {
                          label: t("employees.print.labels.birthDate", {
                            defaultValue: "Birth Date",
                          }),
                          value: printEmployee.birth_date
                            ? new Date(
                                printEmployee.birth_date,
                              ).toLocaleDateString(locale, {
                                day: "2-digit",
                                month: "long",
                                year: "numeric",
                              })
                            : "—",
                        },
                        {
                          label: t("employees.print.labels.gender", {
                            defaultValue: "Gender",
                          }),
                          value:
                            printEmployee.gender === "female"
                              ? t("employees.common.gender.female", {
                                  defaultValue: "Female",
                                })
                              : t("employees.common.gender.male", {
                                  defaultValue: "Male",
                                }),
                        },
                        {
                          label: t("employees.print.labels.department", {
                            defaultValue: "Department",
                          }),
                          value: printEmployee.department || "—",
                        },
                        {
                          label: t("employees.print.labels.position", {
                            defaultValue: "Position",
                          }),
                          value: printEmployee.position || "—",
                        },
                        {
                          label: t("employees.print.labels.shift", {
                            defaultValue: "Work Shift",
                          }),
                          value: printEmployee.work_shift
                            ? printEmployee.work_shift.name
                            : "—",
                        },
                        {
                          label: t("employees.print.labels.status", {
                            defaultValue: "Status",
                          }),
                          value: printEmployee.is_active
                            ? t("employees.common.status.active", {
                                defaultValue: "Active",
                              })
                            : t("employees.common.status.inactive", {
                                defaultValue: "Inactive",
                              }),
                          accent: true,
                        },
                      ]
                        .filter(row => row.value && row.value !== "—")
                        .map(row => (
                          <div
                            key={row.label}
                            style={{
                              display: "flex",
                              borderBottom: "1px solid #000",
                              minHeight: "38px",
                            }}
                          >
                            <div
                              style={{
                                width: "30%",
                                borderLeft: "1px solid #000",
                                backgroundColor: "rgba(227, 242, 253, 0.4)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-start",
                                textAlign: "left",
                                fontWeight: "700",
                                fontSize: "14px",
                                direction: isRTL ? "rtl" : "ltr",
                                color: "#1976d2",
                                fontFamily:
                                  '"Times New Roman Bold", "Times New Roman", "Times", serif',
                                fontWeight: "bold",
                                padding: "0 10px",
                              }}
                            >
                              {row.label}
                            </div>
                            <div
                              style={{
                                flex: 1,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-start",
                                textAlign: "left",
                                fontSize: "15px",
                                fontWeight: row.accent ? "700" : "500",
                                color: row.accent ? "#0057c2" : "#000",
                                direction: isRTL ? "rtl" : "ltr",
                                padding: "0 10px",
                                backgroundColor: "rgba(255, 255, 255, 0.3)",
                                fontFamily:
                                  '"Times New Roman Bold", "Times New Roman", "Times", serif',
                                fontWeight: "bold",
                                wordBreak: row.wrap ? "break-all" : "normal",
                                whiteSpace: row.wrap ? "normal" : "nowrap",
                              }}
                            >
                              {row.value}
                            </div>
                          </div>
                        ));
                    })()}
                  </div>
                </div>

                <div
                  style={{
                    position: "absolute",
                    bottom: "10px",
                    left: "18px",
                    right: "18px",
                    flexShrink: 0,
                  }}
                >
                  <img
                    src={`${publicUrl}/FooterAce.png`}
                    alt="Footer"
                    style={{
                      width: "100%",
                      height: "auto",
                      objectFit: "contain",
                        borderTop: "4px double #000",
                        paddingTop: "8px",
                    }}
                    crossOrigin="anonymous"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* All Employees Print Template */}
      {pdfType === "all" &&
        pdfChunks &&
        pdfChunks.length > 0 &&
        pdfChunks.map((chunk, chunkIndex) => (
          <AllEmployeesPrintTemplate
            key={chunkIndex}
            employees={chunk}
            chunkIndex={chunkIndex}
            allEmployeesList={allEmployeesForPreview}
            employeesPagination={employeesPagination}
            isRTL={isRTL}
            t={t}
            locale={locale}
            publicUrl={publicUrl}
            headerLogo={headerLogo}
          />
        ))}

      {/* Preview Modal */}
      <PdfPreviewModal
        isOpen={
          isPreviewOpen &&
          allEmployeesForPreview &&
          allEmployeesForPreview.length > 0
        }
        title={t("employees.title", { defaultValue: "Employees" })}
        onClose={() => {
          setIsPreviewOpen(false);
          setAllEmployeesForPreview([]);
        }}
        onPrint={handlePrintAll}
        isGeneratingPdf={isGeneratingPdf}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            alignItems: "center",
          }}
        >
          {pdfChunks &&
            pdfChunks.length > 0 &&
            pdfChunks.map((chunk, chunkIdx) => (
                <div
                  key={chunkIdx}
                  style={{
                    width: "794px",
                    height: "auto",
                    backgroundColor: "#ffffff",
                    color: "#000000",
                    fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                    fontWeight: "bold",
                    padding: "40px",
                    boxSizing: "border-box",
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.5)",
                  }}
                >
                  <div style={{ border: "5px solid #000", padding: "3px", backgroundColor: "#ffffff", boxSizing: "border-box", width: "100%" }}>
                    <div
                      style={{
                        border: "2px solid #000",
                        padding: "18px 18px 10px",
                        position: "relative",
                        fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                        fontWeight: "bold",
                        direction: isRTL ? "rtl" : "ltr",
                        backgroundColor: "#ffffff",
                        boxSizing: "border-box",
                        width: "100%",
                      }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          backgroundImage: `url(${headerLogo})`,
                          backgroundSize: "60%",
                          backgroundPosition: "center top",
                          backgroundRepeat: "no-repeat",
                          opacity: 0.05,
                          zIndex: 0,
                          pointerEvents: "none",
                        }}
                      />
                      <div style={{ position: "relative", zIndex: 1 }}>
                        <img
                          src={`${publicUrl}/HeaderAce.png`}
                          alt="Header"
                          style={{ width: "100%", height: "auto", objectFit: "contain", marginBottom: "8px", borderBottom: "4px double #000", paddingBottom: "8px", display: "block" }}
                          crossOrigin="anonymous"
                        />
                        <div
                          style={{
                            marginTop: "12px",
                            direction: isRTL ? "rtl" : "ltr",
                            fontFamily: '"Times New Roman", "Times", serif',
                          }}
                        >
                            {chunk && chunk.length > 0 ? (
                              <table
                                style={{
                                  width: "100%",
                                  borderCollapse: "collapse",
                                  fontSize: "11px",
                                  border: "1px solid #000",
                                  fontFamily:
                                    '"Times New Roman", "Times", serif',
                                  backgroundColor: "rgba(255, 255, 255, 0.7)",
                                  tableLayout: "fixed",
                                }}
                              >
                                <colgroup>
                                  <col style={{ width: "3%" }} />
                                  <col style={{ width: "14%" }} />
                                  <col style={{ width: "8%" }} />
                                  <col style={{ width: "8%" }} />
                                  <col style={{ width: "12%" }} />
                                  <col style={{ width: "11%" }} />
                                  <col style={{ width: "12%" }} />
                                  <col style={{ width: "16%" }} />
                                  <col style={{ width: "10%" }} />
                                  <col style={{ width: "6%" }} />
                                </colgroup>
                                <thead>
                                  <tr
                                    style={{
                                      backgroundColor:
                                        "rgba(238, 242, 247, 0.7)",
                                    }}
                                  >
                                    <th
                                      style={{
                                        border: "1px solid #000",
                                        padding: "6px 2px",
                                        textAlign: "center",
                                        verticalAlign: "middle",
                                        fontWeight: "700",
                                        backgroundColor:
                                          "rgba(238, 242, 247, 0.7)",
                                        overflow: "hidden",
                                        whiteSpace: "normal",
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      #
                                    </th>
                                    <th
                                      style={{
                                        border: "1px solid #000",
                                        padding: "6px 2px",
                                        textAlign: "center",
                                        verticalAlign: "middle",
                                        fontWeight: "700",
                                        backgroundColor:
                                          "rgba(238, 242, 247, 0.7)",
                                        overflow: "hidden",
                                        whiteSpace: "normal",
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      {t("employees.list.table.name", {
                                        defaultValue: "Employee Name",
                                      })}
                                    </th>
                                    <th
                                      style={{
                                        border: "1px solid #000",
                                        padding: "6px 2px",
                                        textAlign: "center",
                                        verticalAlign: "middle",
                                        fontWeight: "700",
                                        backgroundColor:
                                          "rgba(238, 242, 247, 0.7)",
                                        overflow: "hidden",
                                        whiteSpace: "normal",
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      {t("employees.list.table.code", {
                                        defaultValue: "Code",
                                      })}
                                    </th>
                                    <th
                                      style={{
                                        border: "1px solid #000",
                                        padding: "6px 2px",
                                        textAlign: "center",
                                        verticalAlign: "middle",
                                        fontWeight: "700",
                                        backgroundColor:
                                          "rgba(238, 242, 247, 0.7)",
                                        overflow: "hidden",
                                        whiteSpace: "normal",
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      {t("employees.list.table.username", {
                                        defaultValue: "Username",
                                      })}
                                    </th>
                                    <th
                                      style={{
                                        border: "1px solid #000",
                                        padding: "6px 2px",
                                        textAlign: "center",
                                        verticalAlign: "middle",
                                        fontWeight: "700",
                                        backgroundColor:
                                          "rgba(238, 242, 247, 0.7)",
                                        overflow: "hidden",
                                        whiteSpace: "normal",
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      {t("employees.list.table.position", {
                                        defaultValue: "Job Title",
                                      })}
                                    </th>
                                    <th
                                      style={{
                                        border: "1px solid #000",
                                        padding: "6px 2px",
                                        textAlign: "center",
                                        verticalAlign: "middle",
                                        fontWeight: "700",
                                        backgroundColor:
                                          "rgba(238, 242, 247, 0.7)",
                                        overflow: "hidden",
                                        whiteSpace: "normal",
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      {t("employees.list.table.phone", {
                                        defaultValue: "Phone",
                                      })}
                                    </th>
                                    <th
                                      style={{
                                        border: "1px solid #000",
                                        padding: "6px 2px",
                                        textAlign: "center",
                                        verticalAlign: "middle",
                                        fontWeight: "700",
                                        backgroundColor:
                                          "rgba(238, 242, 247, 0.7)",
                                        overflow: "hidden",
                                        whiteSpace: "normal",
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      {t("employees.list.table.nationalId", {
                                        defaultValue: "National ID",
                                      })}
                                    </th>
                                    <th
                                      style={{
                                        border: "1px solid #000",
                                        padding: "6px 2px",
                                        textAlign: "center",
                                        verticalAlign: "middle",
                                        fontWeight: "700",
                                        backgroundColor:
                                          "rgba(238, 242, 247, 0.7)",
                                        overflow: "hidden",
                                        whiteSpace: "normal",
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      {t("employees.list.table.email", {
                                        defaultValue: "Email",
                                      })}
                                    </th>
                                    <th
                                      style={{
                                        border: "1px solid #000",
                                        padding: "6px 2px",
                                        textAlign: "center",
                                        verticalAlign: "middle",
                                        fontWeight: "700",
                                        backgroundColor:
                                          "rgba(238, 242, 247, 0.7)",
                                        overflow: "hidden",
                                        whiteSpace: "normal",
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      {t("employees.list.table.department", {
                                        defaultValue: "Department",
                                      })}
                                    </th>
                                    <th
                                      style={{
                                        border: "1px solid #000",
                                        padding: "6px 2px",
                                        textAlign: "center",
                                        verticalAlign: "middle",
                                        fontWeight: "700",
                                        backgroundColor:
                                          "rgba(238, 242, 247, 0.7)",
                                        overflow: "hidden",
                                        whiteSpace: "normal",
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      {t("employees.list.table.status", {
                                        defaultValue: "Status",
                                      })}
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {chunk.map((employee, idx) => {
                                    // العثور على الترتيب الحقيقي للموظف في القائمة الكاملة
                                    const foundIdx = Array.isArray(
                                      allEmployeesForPreview,
                                    )
                                      ? allEmployeesForPreview.findIndex(
                                          e =>
                                            String(e?.id) ===
                                            String(employee?.id),
                                        )
                                      : -1;

                                    // إيجاد الرقم التسلسلي تصاعدياً من 1
                                    let serialNumber = 0;
                                    if (foundIdx >= 0) {
                                      serialNumber = foundIdx + 1;
                                    } else {
                                      serialNumber = chunkIdx * 25 + idx + 1;
                                    }

                                    const cell = {
                                      border: "1px solid #000",
                                      padding: "5px 2px",
                                      fontSize: "10px",
                                      textAlign: "center",
                                      verticalAlign: "middle",
                                      overflow: "hidden",
                                      whiteSpace: "normal",
                                      wordBreak: "break-word",
                                      backgroundColor:
                                        idx % 2 === 0
                                          ? "rgba(255,255,255,0.7)"
                                          : "rgba(245,245,245,0.5)",
                                    };
                                    return (
                                      <tr key={employee.id}>
                                        <td style={{ ...cell }}>
                                          {serialNumber}
                                        </td>
                                        <td
                                          style={{
                                            ...cell,
                                            direction: isRTL ? "rtl" : "ltr",
                                          }}
                                        >
                                          {employee.name || "—"}
                                        </td>
                                        <td style={{ ...cell }}>
                                          {employee.employee_code ||
                                            employee.id ||
                                            "—"}
                                        </td>
                                        <td style={{ ...cell }}>
                                          {employee.username || "—"}
                                        </td>
                                        <td
                                          style={{
                                            ...cell,
                                            direction: isRTL ? "rtl" : "ltr",
                                          }}
                                        >
                                          {employee.position || "—"}
                                        </td>
                                        <td
                                          style={{ ...cell, direction: "ltr" }}
                                        >
                                          {employee.phone || "—"}
                                        </td>
                                        <td
                                          style={{ ...cell, direction: "ltr" }}
                                        >
                                          {employee.national_id || "—"}
                                        </td>
                                        <td
                                          style={{ ...cell, direction: "ltr" }}
                                        >
                                          {employee.email || "—"}
                                        </td>
                                        <td
                                          style={{
                                            ...cell,
                                            direction: isRTL ? "rtl" : "ltr",
                                          }}
                                        >
                                          {employee.department || "—"}
                                        </td>
                                        <td style={{ ...cell }}>
                                          {employee.is_active
                                            ? t(
                                                "employees.common.status.active",
                                                { defaultValue: "Active" },
                                              )
                                            : t(
                                                "employees.common.status.inactive",
                                                { defaultValue: "Inactive" },
                                              )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            ) : (
                              <div
                                style={{ textAlign: "center", padding: "20px" }}
                              >
                                {t("employees.print.noData", {
                                  defaultValue: "No data available",
                                })}
                              </div>
                            )}
                        </div>
                        <img
                          src={`${publicUrl}/FooterAce.png`}
                          alt="Footer"
                          style={{ width: "100%", height: "auto", objectFit: "contain", display: "block", marginTop: "12px", borderTop: "4px double #000", paddingTop: "8px" }}
                          crossOrigin="anonymous"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
        </div>
      </PdfPreviewModal>

      {/* Single Employee Preview Modal */}
      <PdfPreviewModal
        isOpen={isSinglePreviewOpen && singleEmployeeForPreview}
        title={t("employees.details.title", {
          defaultValue: "Employee Details",
        })}
        onClose={() => {
          setIsSinglePreviewOpen(false);
          setSingleEmployeeForPreview(null);
        }}
        onPrint={() => {
          if (singleEmployeeForPreview) {
            handlePrintEmployee(singleEmployeeForPreview);
          }
        }}
        isGeneratingPdf={isGeneratingPdf}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            alignItems: "center",
          }}
        >
          {singleEmployeeForPreview &&
            employeeForPrint &&
            pdfType === "single" && (
              <div
                id={`preview-employee-pdf-${singleEmployeeForPreview.id}`}
                style={{
                  width: "794px",
                  height: "1123px",
                  backgroundColor: "#ffffff",
                  color: "#000000",
                  fontFamily:
                    '"Times New Roman Bold", "Times New Roman", "Times", serif',
                  fontWeight: "bold",
                  padding: "40px",
                  boxSizing: "border-box",
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.5)",
                }}
              >
                <div
                  style={{
                    border: "5px solid #000",
                    padding: "3px",
                    backgroundColor: "#ffffff",
                    boxSizing: "border-box",
                    width: "100%",
                    minHeight: "1043px",
                    height: "auto",
                    overflow: "visible",
                  }}
                >
                  <div
                    style={{
                      border: "2px solid #000",
                      minHeight: "1033px",
                      height: "1033px",
                      padding: "18px 18px 10px",
                      position: "relative",
                      display: "flex",
                      flexDirection: "column",
                      fontFamily:
                        '"Times New Roman Bold", "Times New Roman", "Times", serif',
                      fontWeight: "bold",
                      WebkitFontSmoothing: "antialiased",
                      MozOsxFontSmoothing: "grayscale",
                      textRendering: "optimizeLegibility",
                      direction: isRTL ? "rtl" : "ltr",
                      backgroundColor: "#ffffff",
                      boxSizing: "border-box",
                      width: "100%",
                      overflow: "visible",
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        backgroundImage: `url(${headerLogo})`,
                        backgroundSize: "60%",
                        backgroundPosition: "center top",
                        backgroundRepeat: "no-repeat",
                        opacity: 0.05,
                        zIndex: 0,
                        pointerEvents: "none",
                      }}
                    />
                    <div
                      style={{
                        position: "relative",
                        zIndex: 1,
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                        minHeight: "100%",
                      }}
                    >
                      <img
                        src={`${publicUrl}/HeaderAce.png`}
                        alt="Header"
                        style={{
                          width: "100%",
                          height: "auto",
                          objectFit: "contain",
                          marginBottom: "8px",
                          borderBottom: "4px double #000",
                          paddingBottom: "8px",
                          flexShrink: 0,
                        }}
                        crossOrigin="anonymous"
                      />

                      <div
                        style={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          overflow: "visible",
                          minHeight: 0,
                          paddingBottom: "120px",
                        }}
                      >
                        <div
                          style={{
                            marginTop: "12px",
                            border: "2px solid #000",
                            borderRadius: "4px",
                            overflow: "hidden",
                            fontFamily:
                              '"Times New Roman Bold", "Times New Roman", "Times", serif',
                            fontWeight: "bold",
                            backgroundColor: "rgba(255, 255, 255, 0.3)",
                          }}
                        >
                          {(() => {
                            const printEmployee =
                              employeeDetails &&
                              employeeDetails.id === singleEmployeeForPreview.id
                                ? employeeDetails
                                : singleEmployeeForPreview;
                            const employeeDate = printEmployee.created_at
                              ? new Date(
                                  printEmployee.created_at,
                                ).toLocaleDateString(locale, {
                                  day: "2-digit",
                                  month: "long",
                                  year: "numeric",
                                })
                              : new Date().toLocaleDateString(locale, {
                                  day: "2-digit",
                                  month: "long",
                                  year: "numeric",
                                });
                            return [
                              {
                                label: t(
                                  "employees.details.print.labels.date",
                                  { defaultValue: "Date" },
                                ),
                                value: employeeDate,
                                accent: true,
                              },
                              {
                                label: t(
                                  "employees.details.print.labels.name",
                                  { defaultValue: "Employee Name" },
                                ),
                                value: printEmployee.name || "—",
                              },
                              {
                                label: t(
                                  "employees.details.print.labels.code",
                                  { defaultValue: "Employee Code" },
                                ),
                                value: printEmployee.employee_code || "—",
                              },
                              {
                                label: t(
                                  "employees.details.print.labels.username",
                                  { defaultValue: "Username" },
                                ),
                                value: printEmployee.username || "—",
                              },
                              {
                                label: t(
                                  "employees.details.print.labels.email",
                                  { defaultValue: "Email" },
                                ),
                                value: printEmployee.email || "—",
                              },
                              {
                                label: t(
                                  "employees.details.print.labels.phone",
                                  { defaultValue: "Phone" },
                                ),
                                value: printEmployee.phone || "—",
                              },
                              {
                                label: t(
                                  "employees.details.print.labels.nationalId",
                                  { defaultValue: "National ID" },
                                ),
                                value: printEmployee.national_id || "—",
                              },
                              {
                                label: t(
                                  "employees.details.print.labels.birthDate",
                                  { defaultValue: "Birth Date" },
                                ),
                                value: printEmployee.birth_date
                                  ? new Date(
                                      printEmployee.birth_date,
                                    ).toLocaleDateString(locale, {
                                      day: "2-digit",
                                      month: "long",
                                      year: "numeric",
                                    })
                                  : "—",
                              },
                              {
                                label: t(
                                  "employees.details.print.labels.gender",
                                  { defaultValue: "Gender" },
                                ),
                                value:
                                  printEmployee.gender === "female"
                                    ? t("employees.common.gender.female", {
                                        defaultValue: "Female",
                                      })
                                    : t("employees.common.gender.male", {
                                        defaultValue: "Male",
                                      }),
                              },
                              {
                                label: t(
                                  "employees.details.print.labels.department",
                                  { defaultValue: "Department" },
                                ),
                                value: printEmployee.department || "—",
                              },
                              {
                                label: t(
                                  "employees.details.print.labels.position",
                                  { defaultValue: "Position" },
                                ),
                                value: printEmployee.position || "—",
                              },
                              {
                                label: t(
                                  "employees.details.print.labels.shift",
                                  { defaultValue: "Work Shift" },
                                ),
                                value: printEmployee.work_shift
                                  ? printEmployee.work_shift.name
                                  : "—",
                              },
                              {
                                label: t(
                                  "employees.details.print.labels.status",
                                  { defaultValue: "Status" },
                                ),
                                value: printEmployee.is_active
                                  ? t("employees.common.status.active", {
                                      defaultValue: "Active",
                                    })
                                  : t("employees.common.status.inactive", {
                                      defaultValue: "Inactive",
                                    }),
                                accent: true,
                              },
                            ]
                              .filter(row => row.value && row.value !== "—")
                              .map(row => (
                                <div
                                  key={row.label}
                                  style={{
                                    display: "flex",
                                    borderBottom: "1px solid #000",
                                    minHeight: "38px",
                                  }}
                                >
                                  <div
                                    style={{
                                      width: "30%",
                                      borderLeft: "1px solid #000",
                                      backgroundColor:
                                        "rgba(227, 242, 253, 0.4)",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "flex-start",
                                      textAlign: "left",
                                      fontWeight: "700",
                                      fontSize: "14px",
                                      direction: isRTL ? "rtl" : "ltr",
                                      color: "#1976d2",
                                      fontFamily:
                                        '"Times New Roman Bold", "Times New Roman", "Times", serif',
                                      fontWeight: "bold",
                                      padding: "0 10px",
                                    }}
                                  >
                                    {row.label}
                                  </div>
                                  <div
                                    style={{
                                      flex: 1,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "flex-start",
                                      textAlign: "left",
                                      fontSize: "15px",
                                      fontWeight: row.accent ? "700" : "500",
                                      color: row.accent ? "#0057c2" : "#000",
                                      direction: isRTL ? "rtl" : "ltr",
                                      padding: "0 10px",
                                      backgroundColor:
                                        "rgba(255, 255, 255, 0.3)",
                                      fontFamily:
                                        '"Times New Roman Bold", "Times New Roman", "Times", serif',
                                      fontWeight: "bold",
                                    }}
                                  >
                                    {row.value}
                                  </div>
                                </div>
                              ));
                          })()}
                        </div>
                      </div>

                      <div
                        style={{
                          position: "absolute",
                          bottom: "10px",
                          left: "18px",
                          right: "18px",
                          flexShrink: 0,
                        }}
                      >
                        <img
                          src={`${publicUrl}/FooterAce.png`}
                          alt="Footer"
                          style={{
                            width: "100%",
                            height: "auto",
                            objectFit: "contain",
              borderTop: "4px double #000",
              paddingTop: "8px",

                          }}
                          crossOrigin="anonymous"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
        </div>
      </PdfPreviewModal>
    </div>
  );
};

// Print Template for All Employees
const AllEmployeesPrintTemplate = ({
  employees,
  chunkIndex = 0,
  allEmployeesList = [],
  employeesPagination,
  isRTL,
  t,
  locale,
  publicUrl,
  headerLogo,
}) => {
  if (!employees || employees.length === 0) return null;

  return (
    <div
      id={`employees-pdf-all-${chunkIndex}`}
      aria-hidden="true"
      style={{
        position: "absolute",
        left: "-9999px",
        top: 0,
        width: "794px",
        height: "auto",
        backgroundColor: "#ffffff",
        color: "#000000",
        fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
        fontWeight: "bold",
        padding: "40px",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          border: "5px solid #000",
          padding: "3px",
          backgroundColor: "#ffffff",
          boxSizing: "border-box",
          width: "100%",
        }}
      >
        <div
          style={{
            border: "2px solid #000",
            padding: "18px 18px 10px",
            position: "relative",
            fontFamily:
              '"Times New Roman Bold", "Times New Roman", "Times", serif',
            fontWeight: "bold",
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
            textRendering: "optimizeLegibility",
            direction: isRTL ? "rtl" : "ltr",
            backgroundColor: "#ffffff",
            boxSizing: "border-box",
            width: "100%",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundImage: `url(${headerLogo})`,
              backgroundSize: "60%",
              backgroundPosition: "center top",
              backgroundRepeat: "no-repeat",
              opacity: 0.05,
              zIndex: 0,
              pointerEvents: "none",
            }}
          />
          <div style={{ position: "relative", zIndex: 1 }}>
            <img
              src={`${publicUrl}/HeaderAce.png`}
              alt="Header"
              style={{
                width: "100%",
                height: "auto",
                objectFit: "contain",
                marginBottom: "8px",
                borderBottom: "4px double #000",
                paddingBottom: "8px",
                display: "block",
              }}
              crossOrigin="anonymous"
            />

            <div style={{ textAlign: "center", padding: "6px 0 8px", borderBottom: "1px solid #ddd", marginBottom: "4px", direction: isRTL ? "rtl" : "ltr" }}>
              <div style={{ fontSize: "16px", fontWeight: "900", color: "#000080" }}>{isRTL ? "تقرير الموظفين" : "Employees Report"}</div>
              <div style={{ fontSize: "11px", color: "#666", marginTop: "3px" }}>{isRTL ? "تاريخ التقرير" : "Report Date"}: {new Date().toLocaleDateString("en-US")}</div>
            </div>

            <div
              style={{
                marginTop: "12px",
                direction: isRTL ? "rtl" : "ltr",
                fontFamily: '"Times New Roman", "Times", serif',
              }}
              >
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "11px",
                    border: "1px solid #000",
                    fontFamily: '"Times New Roman", "Times", serif',
                    backgroundColor: "rgba(255, 255, 255, 0.7)",
                    tableLayout: "fixed",
                  }}
                >
                  <colgroup>
                    <col style={{ width: "3%" }} />
                    <col style={{ width: "14%" }} />
                    <col style={{ width: "8%" }} />
                    <col style={{ width: "8%" }} />
                    <col style={{ width: "12%" }} />
                    <col style={{ width: "11%" }} />
                    <col style={{ width: "12%" }} />
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "6%" }} />
                  </colgroup>
                  <thead>
                    <tr style={{ backgroundColor: "rgba(238, 242, 247, 0.7)" }}>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px 2px",
                          textAlign: "center",
                          fontWeight: "700",
                          backgroundColor: "rgba(238, 242, 247, 0.7)",
                          overflow: "hidden",
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                        }}
                      >
                        #
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px 2px",
                          textAlign: "center",
                          fontWeight: "700",
                          backgroundColor: "rgba(238, 242, 247, 0.7)",
                          overflow: "hidden",
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                        }}
                      >
                        {t("employees.list.table.name", {
                          defaultValue: "Employee Name",
                        })}
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px 2px",
                          textAlign: "center",
                          fontWeight: "700",
                          backgroundColor: "rgba(238, 242, 247, 0.7)",
                          overflow: "hidden",
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                        }}
                      >
                        {t("employees.list.table.code", {
                          defaultValue: "Code",
                        })}
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px 2px",
                          textAlign: "center",
                          fontWeight: "700",
                          backgroundColor: "rgba(238, 242, 247, 0.7)",
                          overflow: "hidden",
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                        }}
                      >
                        {t("employees.list.table.username", {
                          defaultValue: "Username",
                        })}
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px 2px",
                          textAlign: "center",
                          fontWeight: "700",
                          backgroundColor: "rgba(238, 242, 247, 0.7)",
                          overflow: "hidden",
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                        }}
                      >
                        {t("employees.list.table.position", {
                          defaultValue: "Job Title",
                        })}
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px 2px",
                          textAlign: "center",
                          fontWeight: "700",
                          backgroundColor: "rgba(238, 242, 247, 0.7)",
                          overflow: "hidden",
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                        }}
                      >
                        {t("employees.list.table.phone", {
                          defaultValue: "Phone",
                        })}
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px 2px",
                          textAlign: "center",
                          fontWeight: "700",
                          backgroundColor: "rgba(238, 242, 247, 0.7)",
                          overflow: "hidden",
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                        }}
                      >
                        {t("employees.list.table.nationalId", {
                          defaultValue: "National ID",
                        })}
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px 2px",
                          textAlign: "center",
                          fontWeight: "700",
                          backgroundColor: "rgba(238, 242, 247, 0.7)",
                          overflow: "hidden",
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                        }}
                      >
                        {t("employees.list.table.email", {
                          defaultValue: "Email",
                        })}
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px 2px",
                          textAlign: "center",
                          fontWeight: "700",
                          backgroundColor: "rgba(238, 242, 247, 0.7)",
                          overflow: "hidden",
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                        }}
                      >
                        {t("employees.list.table.department", {
                          defaultValue: "Department",
                        })}
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px 2px",
                          textAlign: "center",
                          fontWeight: "700",
                          backgroundColor: "rgba(238, 242, 247, 0.7)",
                          overflow: "hidden",
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                        }}
                      >
                        {t("employees.list.table.status", {
                          defaultValue: "Status",
                        })}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {employees.map((employee, idx) => {
                      const foundIdx = Array.isArray(allEmployeesList)
                        ? allEmployeesList.findIndex(
                            e => String(e?.id) === String(employee?.id),
                          )
                        : -1;

                      let serialNumber = 0;
                      if (foundIdx >= 0) {
                        serialNumber = foundIdx + 1;
                      } else {
                        // Fallback if not found
                        let previousItemsCount = 0;
                        if (allEmployeesList && allEmployeesList.length > 0) {
                          previousItemsCount = chunkIndex * 15; // fallback approximation
                        }
                        serialNumber = previousItemsCount + idx + 1;
                      }

                      const cellBase = {
                        border: "1px solid #000",
                        padding: "5px 2px",
                        fontSize: "10px",
                        textAlign: "center",
                        verticalAlign: "middle",
                        overflow: "hidden",
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        backgroundColor:
                          idx % 2 === 0
                            ? "rgba(255,255,255,0.7)"
                            : "rgba(245,245,245,0.5)",
                      };
                      return (
                        <tr key={employee.id}>
                          <td style={{ ...cellBase }}>{serialNumber}</td>
                          <td
                            style={{
                              ...cellBase,
                              direction: isRTL ? "rtl" : "ltr",
                            }}
                          >
                            {employee.name || "—"}
                          </td>
                          <td style={{ ...cellBase }}>
                            {employee.employee_code || "—"}
                          </td>
                          <td style={{ ...cellBase }}>
                            {employee.username || "—"}
                          </td>
                          <td
                            style={{
                              ...cellBase,
                              direction: isRTL ? "rtl" : "ltr",
                            }}
                          >
                            {employee.position || "—"}
                          </td>
                          <td style={{ ...cellBase, direction: "ltr" }}>
                            {employee.phone || "—"}
                          </td>
                          <td style={{ ...cellBase, direction: "ltr" }}>
                            {employee.national_id || "—"}
                          </td>
                          <td style={{ ...cellBase, direction: "ltr" }}>
                            {employee.email || "—"}
                          </td>
                          <td
                            style={{
                              ...cellBase,
                              direction: isRTL ? "rtl" : "ltr",
                            }}
                          >
                            {employee.department || "—"}
                          </td>
                          <td style={{ ...cellBase }}>
                            {employee.is_active
                              ? t("employees.common.status.active", {
                                  defaultValue: "Active",
                                })
                              : t("employees.common.status.inactive", {
                                  defaultValue: "Inactive",
                                })}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
            </div>

            <img
              src={`${publicUrl}/FooterAce.png`}
              alt="Footer"
              style={{ width: "100%", height: "auto", objectFit: "contain", display: "block", marginTop: "12px", borderTop: "4px double #000", paddingTop: "8px" }}
              crossOrigin="anonymous"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Employees;
